import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { IconButton } from "@mui/material";
import { InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";

import TableFilter from "components/Shared/AppTable/TableFilter";
import { localizeDateShort } from "utils/dateFormats";
import { BaseTabs } from "components/Shared/BaseTabs";
import FileIcon from "components/Shared/FileIcon";
import AppTable from "components/Shared/AppTable";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";

import { useLazyListDriverForLicencesQuery } from "store/services/driverApi";

import { useAppMeta, useTablePagination } from "hooks";

import ExportDriverLicenceExcel from "./ExportDriverLicenceExcel";

const DriverLicenceListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Active";

  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [
    {
      title: "Reports",
      path: "/app/crm/reports/list",
    },
    {
      title: "Driver Licences",
    },
  ];

  const driverLicenceListTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
    { label: "Expiring Soon", value: "Expiring Soon" },
    { label: "Expired", value: "Expired" },
  ];

  const [fetchDrivers, { data: driverRes, isLoading, isFetching, error }] =
    useLazyListDriverForLicencesQuery();

  React.useEffect(() => {
    fetchDrivers({
      Active: "true",
      licenseStatus: activeTab,
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
    });
  }, [activeTab, currentPage, fetchDrivers, keyword, perPage]);

  const columns = [
    {
      name: "Licence ID",
      selector: (row) => row?.driverLicence?.licenceIdd,
      cell: (row) => row?.driverLicence?.licenceIdd || "N/A",
    },
    {
      name: "Driver Name",
      selector: (row) => row?.name,
      cell: (row) => row?.name,
    },
    {
      name: "Driver ID",
      selector: (row) => row?.driverId,
      cell: (row) => row?.driverId,
    },
    {
      name: "Licence Number",
      selector: (row) => row?.driverLicence?.licenceNumber,
      cell: (row) => row?.driverLicence?.licenceNumber || "N/A",
    },
    {
      name: "Issue Date",
      selector: (row) => row?.driverLicence?.issueDate,
      cell: (row) =>
        row?.driverLicence?.issueDate
          ? localizeDateShort(row.driverLicence.issueDate)
          : "N/A",
    },
    {
      name: "Expiry Date",
      selector: (row) => row?.driverLicence?.expiryDate,
      cell: (row) =>
        row?.driverLicence?.expiryDate
          ? localizeDateShort(row.driverLicence.expiryDate)
          : "N/A",
    },
    {
      name: "Type",
      selector: (row) => row?.driverLicence?.type,
      cell: (row) => row?.driverLicence?.type || "N/A",
    },
    {
      name: "Status",
      selector: (row) => row?.driverLicence?.status,
      cell: (row) => row?.driverLicence?.status || "N/A",
    },
    {
      name: "Attachments",
      selector: (row) => row?.driverLicence?.attachments,
      cell: (row) => (
        <div className="flex gap-4">
          {row?.driverLicence?.attachments?.map((attachment) => (
            <a
              key={attachment?._id}
              href={attachment?.url}
              target="_blank"
              rel="noreferrer"
              style={{ color: "var(--primary)" }}
            >
              <FileIcon fileName={attachment?.name} fileUrl={attachment?.url} />
            </a>
          ))}
          {(!row?.driverLicence?.attachments ||
            row?.driverLicence?.attachments.length === 0) && (
            <span>No attachments</span>
          )}
        </div>
      ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/driver/${row._id}/detail?tab=licence`}>
            <IconButton size="small" aria-label="View Licence" color="primary">
              <InfoOutlined />
            </IconButton>
          </Link>
          <Link to={`/app/crm/driver/${row._id}/detail?tab=licence`}>
            <IconButton size="small" aria-label="Edit Licence">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  useAppMeta(`${activeTab} Driver Licences`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs}>
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <BaseTabs
          tabs={driverLicenceListTabs}
          defaultValue={activeTab}
          actions={<ExportDriverLicenceExcel activeTab={activeTab} />}
        />
        <TableFilter
          searchLabel="Name/Display Name/Email"
          disabled={isLoading || isFetching}
        >
          <div className="font-medium"> {activeTab} Driver Licences </div>
        </TableFilter>
        <AppTable
          columns={columns}
          data={driverRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={driverRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default DriverLicenceListScreen;
